import React from 'react';
import { Phone, MessageCircle, Mail, MapPin, Clock } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const ContactPage = () => {
  // FAQ data for contact page schema
  const contactFAQs = [
    {
      question: "How quickly will Amy respond to my inquiry?",
      answer: "I personally respond to every call, text, and email within 1 business day. You won't be passed to a paralegal or receptionist—you'll speak directly with me."
    },
    {
      question: "What should I bring to my free consultation?",
      answer: "Gather any accident reports, medical records, insurance correspondence, and photos you have. Don't worry if you don't have everything—I can help you obtain missing documents."
    },
    {
      question: "Do I have to come to the office for a consultation?",
      answer: "No. I offer phone consultations and can meet virtually. For clients throughout Washington State and Alaska, this flexibility ensures everyone has access to quality legal representation."
    },
    {
      question: "What happens during a free consultation?",
      answer: "I'll listen to your story, review your situation, explain your legal options, and give you an honest assessment of your case. There's no obligation—even if I'm not the right fit, I'll point you in the right direction."
    }
  ];

  // HowTo schema for getting started
  const howToSteps = {
    name: "How to Get a Free Consultation with Amy C. Brown Law",
    description: "Simple steps to connect with a personal injury attorney and start your case evaluation.",
    totalTime: "PT10M",
    steps: [
      { name: "Call, Text, or Email", text: "Reach out via phone at (425) 675-1224, text the same number, or email amy@amycbrownlaw.com. I personally respond to every inquiry." },
      { name: "Describe Your Situation", text: "Briefly explain what happened, when it occurred, and what injuries you sustained. This helps me understand if I can help." },
      { name: "Schedule Your Free Consultation", text: "We'll set up a time to discuss your case in detail. Consultations are always free with no obligation." },
      { name: "Bring Your Documents", text: "Gather any accident reports, medical records, insurance correspondence, and photos you have. Don't worry if you don't have everything." },
      { name: "Get Your Case Evaluation", text: "I'll review your situation, explain your legal options, and give you an honest assessment of your case's potential value." }
    ]
  };

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <SEOHead 
        title="Contact Amy C. Brown | Free Consultation | (425) 675-1224"
        description="Contact Amy C. Brown Law for a free personal injury consultation. Call, text, or email. Personal response within 1 business day. Everett, WA serving all of Washington and Alaska."
        howToData={howToSteps}
        faqData={contactFAQs}
      />
      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-6">
              Contact
            </h1>
            <p className="text-xl text-slate-600 mb-4">
              Call now for a free consultation!
            </p>
            <a 
              href="tel:425-675-1224"
              className="text-4xl md:text-5xl font-heading font-bold text-primary hover:text-primary/80 transition-colors"
              data-testid="contact-hero-phone"
            >
              (425) 675-1224
            </a>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <p className="text-xl text-slate-600">
              Tap to call, text, or email me directly. <strong>I personally respond to every inquiry within 1 business day.</strong>
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {/* Call */}
            <a 
              href="tel:425-675-1224"
              className="group bg-slate-50 rounded-2xl p-8 text-center hover-lift border border-slate-100 hover:border-teal-200 transition-all"
              data-testid="contact-call-card"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-500 transition-colors">
                <Phone className="w-8 h-8 text-teal-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-secondary mb-2">Call</h2>
              <p className="text-teal-600 font-semibold text-lg">(425) 675-1224</p>
              <p className="text-slate-500 text-sm mt-2">Tap to call now</p>
            </a>

            {/* Text */}
            <a 
              href="sms:425-675-1224"
              className="group bg-slate-50 rounded-2xl p-8 text-center hover-lift border border-slate-100 hover:border-teal-200 transition-all"
              data-testid="contact-text-card"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-500 transition-colors">
                <MessageCircle className="w-8 h-8 text-teal-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-secondary mb-2">Text</h2>
              <p className="text-teal-600 font-semibold text-lg">(425) 675-1224</p>
              <p className="text-slate-500 text-sm mt-2">Tap to send a text</p>
            </a>

            {/* Email */}
            <a 
              href="mailto:amy@amycbrownlaw.com"
              className="group bg-slate-50 rounded-2xl p-8 text-center hover-lift border border-slate-100 hover:border-teal-200 transition-all"
              data-testid="contact-email-card"
            >
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-teal-500 transition-colors">
                <Mail className="w-8 h-8 text-teal-600 group-hover:text-white transition-colors" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-secondary mb-2">Email</h2>
              <p className="text-teal-600 font-semibold text-lg break-all">amy@amycbrownlaw.com</p>
              <p className="text-slate-500 text-sm mt-2">Tap to send an email</p>
            </a>
          </div>
        </div>
      </section>

      {/* Additional Info */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <MapPin className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2">Location</h3>
              <p className="text-slate-600">
                Everett, Washington<br />
                Serving Washington State & Alaska
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <Clock className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2">Response Time</h3>
              <p className="text-slate-600">
                I personally respond to<br />
                every inquiry within 1 business day
              </p>
            </div>

            <div className="text-center md:col-span-2 lg:col-span-1">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-md">
                <Phone className="w-8 h-8 text-teal-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-secondary mb-2">Direct Line</h3>
              <p className="text-slate-600">
                No receptionists or paralegals—<br />
                you reach me directly
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
            Ready to Discuss Your Case?
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            I'll listen, explain your options clearly, and handle the complexities so you can heal.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:425-675-1224"
              className="inline-flex items-center justify-center gap-2 bg-white text-teal-700 px-8 py-4 rounded-full font-semibold hover:bg-teal-50 transition-all duration-300 shadow-lg"
              data-testid="contact-final-call"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a 
              href="sms:425-675-1224"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
            >
              <MessageCircle className="w-5 h-5" />
              Text (425) 675-1224
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
